/**
 * Product Service
 * Frontend service that fetches products from backend API
 * All pricing logic and critical business rules are handled server-side
 */

import UniversalApiClient from './UniversalApiClient';
import ApiService from './ApiService';

class ProductService {
  // Request deduplication - store in-flight requests
  static _pendingRequests = new Map();

  /**
   * Get product catalog with server-side processing
   * Includes request deduplication to prevent multiple simultaneous calls with same params
   * @param {Object} params - Query parameters (page, limit, search, category, sortBy)
   */
  static async getCatalog(params = {}) {
    const { page = 1, limit = 50, search = '', category = '', sortBy = 'name' } = params;
    
    // Create cache key from params
    const cacheKey = JSON.stringify({ page, limit, search, category, sortBy });
    
    // Check if there's already a pending request with same params
    if (this._pendingRequests.has(cacheKey)) {
      console.log('[ProductService.getCatalog] ⏳ Request already in progress, returning existing promise');
      return this._pendingRequests.get(cacheKey);
    }

    try {
      console.log('[ProductService.getCatalog] 🆕 New request with params:', { page, limit, search, category, sortBy });
      
      // Create and store the promise
      const requestPromise = (async () => {
        // Updated to use user-actions middleman endpoint (no direct database exposure)
        const response = await UniversalApiClient.strapiGet('/user-actions/catalog', {
          page,
          limit,
          search,
          category,
          sortBy
        });

        console.log('[ProductService.getCatalog] ✅ Response received:', {
          success: response.success,
          status: response.status,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
          productCount: response.data?.products?.length || 0
        });

        // Return the universal envelope as-is: { success, data, status }
        // Callers should read products from response.data.products
        if (!response.success) {
          const errorMsg = response.error?.message || 'Catalog fetch failed';
          console.error('[ProductService.getCatalog] Request failed:', errorMsg);
          throw new Error(errorMsg);
        }

        if (!response.data || !response.data.products) {
          console.error('[ProductService.getCatalog] Invalid response structure:', response);
          throw new Error('Invalid catalog response structure');
        }

        return response;
      })();

      // Store the promise
      this._pendingRequests.set(cacheKey, requestPromise);

      // Wait for it to complete
      const result = await requestPromise;

      // Clean up after completion (after a small delay to allow duplicate requests to use it)
      setTimeout(() => {
        this._pendingRequests.delete(cacheKey);
      }, 100);

      return result;
    } catch (error) {
      // Clean up on error
      this._pendingRequests.delete(cacheKey);
      
      console.error('[ProductService.getCatalog] Exception:', error);
      console.error('[ProductService.getCatalog] Error details:', error.response || error);
      throw error;
    }
  }

  /**
   * Validate product before redemption (server-side validation)
   * @param {string} productRef - Opaque product reference (server-issued)
   * @param {string} userId - User ID (optional)
   */
  static async validateProduct(productRef, userId = null) {
    try {
      // Updated to use user-actions middleman endpoint (no direct database exposure)
      // SECURITY: Do NOT send identity fields (userId/user/id) in the body.
      // The backend derives identity from the httpOnly auth cookie.
      // Keeping the `userId` parameter only for backward compatibility at call sites.
      const response = await UniversalApiClient.strapiPost('/user-actions/validate', {
        productRef,
      });

      // Handle network/server errors
      if (!response.success && response.error) {
        throw new Error(response.error?.message || 'Network error during validation');
      }

      // Response shape: { success: true/false, valid: true/false, message, product, failedChecks }
      const body = response.data || response;
      
      // Normalize to expected shape used by Catalog: { isValid, errors, product }
      if (body?.valid === true) {
        return {
          isValid: true,
          errors: [],
          product: body.product
        };
      }

      // Validation failed (but request succeeded)
      return {
        isValid: false,
        errors: body?.failedChecks || [body?.message || 'Product validation failed']
      };
    } catch (error) {
      console.error('Error validating product:', error);
      throw error;
    }
  }

  /**
   * Calculate price (server-side calculation for security)
   * @param {number} faceValue - Gift card face value
   * @param {number} quantity - Quantity to purchase
   */
  static async calculatePrice(faceValue, quantity = 1) {
    try {
      const response = await UniversalApiClient.strapiPost('/products/calculate-price', {
        faceValue,
        quantity
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Price calculation failed');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error calculating price:', error);
      throw error;
    }
  }

  /**
   * Redeem gift card (through Strapi backend)
   * @param {string} productRef - Opaque product reference (server-issued)
   * @param {number} quantity - Quantity to redeem
   */
  static async redeemGiftCard(productRef, quantity = 1) {
    try {
      console.log('🎁 Redeeming gift card:', { productRef, quantity });
      
      // Backend gets userId from JWT token (ctx.state.user), not from body
      // Updated to use user-actions middleman endpoint (no direct database exposure)
      const response = await UniversalApiClient.strapiPost('/user-actions/redeem', {
        productRef,
        quantity
      });

      console.log('✅ Redemption response:', response);

      if (!response.success) {
        // Extract the most specific error message available.
        // The UniversalApiClient error interceptor normalises server errors into
        // { message, status, details, ... } on response.error.
        const msg =
          response.error?.message ||
          response.error?.details?.error?.message ||
          response.message ||
          'Redemption failed';
        throw new Error(msg);
      }
      const raw = {
        success: true,
        message: response.message || 'Redemption successful!',
        remainingPoints: response.data?.remainingPoints,
        pointsDeducted: response.data?.pointsDeducted,
        redemptionCodes: response.data?.redemptionCodes,
        orderName: response.data?.orderName,
        previousPoints: response.data?.previousPoints
      };
      // Return unified normalized mutation
      return ApiService.normalizePointMutation(raw, 'redemption');
    } catch (error) {
      console.error('❌ Error redeeming gift card:', error);
      throw error;
    }
  }

  /**
   * Search products
   * @param {string} query - Search query
   * @param {Object} filters - Additional filters
   */
  static async searchProducts(query, filters = {}) {
    try {
      return await this.getCatalog({
        ...filters,
        search: query
      });
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Get products by category
   * @param {string} category - Category name
   * @param {Object} params - Additional parameters
   */
  static async getProductsByCategory(category, params = {}) {
    try {
      return await this.getCatalog({
        ...params,
        category
      });
    } catch (error) {
      console.error('Error fetching products by category:', error);
      throw error;
    }
  }

  /**
   * Get products by brand
   * @param {string} brand - Brand name
   * @param {Object} params - Additional parameters
   */
  static async getProductsByBrand(brand, params = {}) {
    try {
      return await this.getCatalog({
        ...params,
        search: brand
      });
    } catch (error) {
      console.error('Error fetching products by brand:', error);
      throw error;
    }
  }

  /**
   * Group products by brand (client-side grouping only)
   * Server provides the data, client organizes for display
   */
  static groupProductsByBrand(products) {
    const groups = {};
    
    products.forEach(product => {
      const brand = product.brand || 'Other';
      
      if (!groups[brand]) {
        groups[brand] = {
          brand,
          products: [],
          image: product.image,
          count: 0
        };
      }
      
      groups[brand].products.push(product);
      groups[brand].count++;
    });

    return Object.values(groups);
  }

  /**
   * Check if product is in stock (has inventory)
   * Note: This checks stock level, not whether product should be shown
   * All products should be displayed regardless of stock status
   */
  static isAvailable(product) {
    return product && product.available > 0;
  }

  /**
   * Get formatted price string
   */
  static getFormattedPrice(product) {
    if (!product || !product.sellingPrice) {
      return 'N/A';
    }
    return `${product.sellingPrice.toLocaleString()} Coins`;
  }

  /**
   * Get formatted face value
   */
  static getFormattedFaceValue(product) {
    if (!product || !product.faceValue) {
      return 'N/A';
    }
    return `$${product.faceValue.toFixed(2)}`;
  }
}

export default ProductService;
